import React, { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useAffiliate, useDeleteAffiliate } from "@/hooks/use-affiliates";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/Card";
import { formatDate, cn } from "@/lib/utils";
import { Trash2, Settings, Radio, Users, Tag, EyeOff } from "lucide-react";
import { AffiliatePostbacksTab } from "./tabs/AffiliatePostbacksTab";
import { AffiliateUsersTab } from "./tabs/AffiliateUsersTab";
import { AffiliateVisibilityTab } from "./tabs/AffiliateVisibilityTab";
import { EntityLabels } from "@/components/EntityLabels";

const tabs = [
  { id: "details", label: "Details", icon: Settings },
  { id: "postbacks", label: "Postbacks", icon: Radio },
  { id: "users", label: "Users", icon: Users },
  { id: "visibility", label: "Offer Visibility", icon: EyeOff },
  { id: "labels", label: "Labels", icon: Tag },
] as const;

type TabId = (typeof tabs)[number]["id"];

export function AffiliateDetailPage() {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();
  const { data, isLoading } = useAffiliate(id!);
  const deleteMutation = useDeleteAffiliate();
  const [activeTab, setActiveTab] = useState<TabId>("details");

  const affiliate = data?.data;

  async function handleDelete() {
    if (!affiliate) return;
    if (!window.confirm(`Are you sure you want to delete "${affiliate.name}"?`)) return;
    await deleteMutation.mutateAsync(affiliate.id);
    navigate({ to: "/affiliates" });
  }

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Loading..." backHref="/affiliates" />
        <div className="animate-pulse space-y-4">
          <div className="h-48 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!affiliate) {
    return (
      <div>
        <PageHeader title="Affiliate Not Found" backHref="/affiliates" />
        <p className="text-gray-500">This affiliate does not exist or has been removed.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <PageHeader
          title={affiliate.name}
          description={`Affiliate #${affiliate.id}`}
          backHref="/affiliates"
        />
        <button
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
          className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors",
                  activeTab === tab.id
                    ? "border-gray-900 text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-700",
                )}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "details" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card title="Details">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Status</dt>
                  <dd className="mt-1"><StatusBadge status={affiliate.status} /></dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Currency</dt>
                  <dd className="mt-1 text-sm text-gray-700">{affiliate.defaultCurrencyId}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Notifications</dt>
                  <dd className="mt-1 text-sm text-gray-700">{affiliate.hasNotifications ? "Enabled" : "Disabled"}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Media Cost Links</dt>
                  <dd className="mt-1 text-sm text-gray-700">{affiliate.enableMediaCostTrackingLinks ? "Enabled" : "Disabled"}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Account Manager</dt>
                  <dd className="mt-1 text-sm text-gray-700">{affiliate.accountManagerId ? `#${affiliate.accountManagerId}` : "None"}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Tier</dt>
                  <dd className="mt-1 text-sm text-gray-700">{affiliate.tierId ? `Tier #${affiliate.tierId}` : "None"}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Payment Type</dt>
                  <dd className="mt-1 text-sm text-gray-700 capitalize">{affiliate.paymentType || "None"}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Billing Frequency</dt>
                  <dd className="mt-1 text-sm text-gray-700 capitalize">{affiliate.billingFrequency || "N/A"}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Created</dt>
                  <dd className="mt-1 text-sm text-gray-700">{formatDate(affiliate.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Updated</dt>
                  <dd className="mt-1 text-sm text-gray-700">{formatDate(affiliate.updatedAt)}</dd>
                </div>
              </dl>
            </Card>
          </div>

          <div>
            {affiliate.internalNotes && (
              <Card title="Internal Notes">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{affiliate.internalNotes}</p>
              </Card>
            )}
          </div>
        </div>
      )}

      {activeTab === "postbacks" && <AffiliatePostbacksTab affiliateId={id!} />}
      {activeTab === "users" && <AffiliateUsersTab affiliateId={id!} />}
      {activeTab === "visibility" && <AffiliateVisibilityTab affiliateId={id!} />}
      {activeTab === "labels" && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <EntityLabels
            entityType="affiliate"
            entityId={id!}
            apiBase="/v1/affiliates"
          />
        </div>
      )}
    </div>
  );
}
